import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { WageProductivityChart } from "./WageProductivityChart";
import { IncomeSharesChart } from "./IncomeSharesChart";
import { IncomeDecilesChart } from "./IncomeDecilesChart";
import { LabourShareChart } from "./LabourShareChart";
import { ManufacturingShareChart } from "./ManufacturingShareChart";
import { MertonBinomial, MertonTimeline, MertonModels, MertonNobel, MertonScheme } from "./MertonCharts";
import { DivergenceGdpPc, DivergenceShare, DivergenceInventions, DivergenceMatrix } from "./DivergenceCharts";
import { RustBeltEmployment, RustBeltCities, RustBeltOutput, RustBeltStates, RustBeltChronology } from "./RustBeltCharts";
import { EconometricsNobel, EconometricsRepec } from "./EconometricsCharts";

const CHARTS = [
  { key: "wages", label: { it: "Salari e produttività", en: "Wages and productivity" }, C: WageProductivityChart },
  { key: "shares", label: { it: "Quote del reddito USA", en: "US income shares" }, C: IncomeSharesChart },
  { key: "deciles", label: { it: "Top 10% e 50% più povero", en: "Top 10% and poorest 50%" }, C: IncomeDecilesChart },
  { key: "labour", label: { it: "Quota del lavoro, 13 Paesi", en: "Labour share, 13 countries" }, C: LabourShareChart },
  { key: "manufacturing", label: { it: "Manifattura sul PIL", en: "Manufacturing in GDP" }, C: ManufacturingShareChart },
];

const MERTON = [
  { key: "binomial", label: { it: "Quanti ricercatori servono", en: "How many researchers it takes" }, C: MertonBinomial },
  { key: "timeline", label: { it: "Atlante dei multipli", en: "Atlas of multiples" }, C: MertonTimeline },
  { key: "models", label: { it: "Caso, comunicazione o tempi maturi", en: "Chance, communication or ripe times" }, C: MertonModels },
  { key: "scheme", label: { it: "Lo schema di Merton", en: "Merton's scheme" }, C: MertonScheme },
  { key: "nobel", label: { it: "Il Nobel e i multipli", en: "The Nobel and multiples" }, C: MertonNobel },
];
const DIVERGENCE = [
  { key: "gdppc", label: { it: "PIL pro capite 1000-1913", en: "GDP per capita 1000-1913" }, C: DivergenceGdpPc },
  { key: "share", label: { it: "Quota del PIL mondiale", en: "Share of world GDP" }, C: DivergenceShare },
  { key: "inventions", label: { it: "Invenzioni cinesi, ritardo europeo", en: "Chinese inventions, European lag" }, C: DivergenceInventions },
  { key: "matrix", label: { it: "Dieci ipotesi a confronto", en: "Ten hypotheses compared" }, C: DivergenceMatrix },
];
const RUSTBELT = [
  { key: "employment", label: { it: "Occupati manifatturiero 1939-2025", en: "Manufacturing jobs 1939-2025" }, C: RustBeltEmployment },
  { key: "cities", label: { it: "Otto città, 1900-2020", en: "Eight cities, 1900-2020" }, C: RustBeltCities },
  { key: "output", label: { it: "Produzione vs occupazione", en: "Output vs employment" }, C: RustBeltOutput },
  { key: "states", label: { it: "Gli otto stati", en: "The eight states" }, C: RustBeltStates },
  { key: "chronology", label: { it: "Cronologia 1825-2026", en: "Chronology 1825-2026" }, C: RustBeltChronology },
];
const ECONOMETRICS = [
  { key: "nobel", label: { it: "Nobel econometrici 1969-2021", en: "Econometric Nobels 1969-2021" }, C: EconometricsNobel },
  { key: "repec", label: { it: "Ranking RePEc per paese", en: "RePEc ranking by country" }, C: EconometricsRepec },
];
const SETS = { labour: CHARTS, merton: MERTON, divergence: DIVERGENCE, rustbelt: RUSTBELT, econometrics: ECONOMETRICS };

export const isLabourFeature = (tags) => (tags || []).includes('editorial') && (tags || []).includes('labour_share');

export const EditorialCharts = ({ lang = "it", articleId, title, set = "labour" }) => {
  const LIST = SETS[set] || CHARTS;
  const [active, setActive] = useState(LIST[0].key);
  const ui = lang === "it" ? "it" : "en";
  const { C } = LIST.find(c => c.key === active);
  return (
    <section className="edc" data-testid="editorial-charts" dir="ltr">
      <header className="edc-head">
        <div className="edc-kicker"><BarChart3 size={13} /> {ui === "it" ? "Grafici interattivi" : "Interactive charts"}</div>
        {articleId && <Link to={`/${lang}/article/${articleId}`} className="edc-link" data-testid="editorial-charts-link">{ui === "it" ? "Leggi l'inchiesta" : "Read the feature"} →</Link>}
      </header>
      {title && <div className="edc-title">{title}</div>}
      <div className="edc-tabs" role="tablist">
        {LIST.map(c => (
          <button key={c.key} type="button" role="tab" aria-selected={active === c.key} className={`edc-tab ${active === c.key ? "active" : ""}`} onClick={() => setActive(c.key)} data-testid={`editorial-chart-tab-${c.key}`}>
            {c.label[ui]}
          </button>
        ))}
      </div>
      <div className="edc-body" data-testid={`editorial-chart-${active}`}><C lang={lang} /></div>
    </section>
  );
};

export default EditorialCharts;
